import React, { useEffect, useState, useCallback } from 'react';
import { BattleAreaProps, GamePhase } from '../types';
import { playSwordSound } from '../utils/sound';
import Sprite from './Sprite';

// BattleArea Component
const BattleArea = ({
    swordClash,
    gamePhase,
    attackingCharacter,
    damagedCharacter,
    onDeathAnimationComplete
}: BattleAreaProps) => {
    const [imageError, setImageError] = useState(false);
    const [playerDeathComplete, setPlayerDeathComplete] = useState(false);
    const [aiDeathComplete, setAiDeathComplete] = useState(false);

    // Play sound effect when sword clash occurs
    useEffect(() => {
        if (swordClash) {
            playSwordSound();
        }
    }, [swordClash]);

    // Attempt to preload the images to check if they exist
    useEffect(() => {
        // Check idle sprite
        const idleImg = new Image();
        idleImg.onload = () => setImageError(false);
        idleImg.onerror = () => setImageError(true);
        idleImg.src = `${process.env.PUBLIC_URL}/images/sprites/pirate-idle.png`;
        const idleImg2 = new Image();
        idleImg2.onload = () => setImageError(false);
        idleImg2.onerror = () => setImageError(true);
        idleImg2.src = `${process.env.PUBLIC_URL}/images/sprites/pirate-idle2.png`;

        // Check attack sprite
        const attackImg = new Image();
        attackImg.src = `${process.env.PUBLIC_URL}/images/sprites/pirate-attack.png`;
        const attackImg2 = new Image();
        attackImg2.src = `${process.env.PUBLIC_URL}/images/sprites/pirate-attack2.png`;

        // Check death sprite
        const deathImg = new Image();
        deathImg.src = `${process.env.PUBLIC_URL}/images/sprites/pirate-death.png`;
        const deathImg2 = new Image();
        deathImg2.src = `${process.env.PUBLIC_URL}/images/sprites/pirate-death2.png`;
    }, []);

    // Reset completion states when damaged character changes
    useEffect(() => {
        if (!damagedCharacter) {
            setPlayerDeathComplete(false);
            setAiDeathComplete(false);
        }
    }, [damagedCharacter]);

    // Handle death animation completion
    const handleDeathAnimationComplete = useCallback((character: 'player' | 'ai') => {
        if (character === 'player') {
            setPlayerDeathComplete(true);
        } else if (character === 'ai') {
            setAiDeathComplete(true);
        }

        // Notify parent component that death animation is complete
        if (onDeathAnimationComplete) {
            onDeathAnimationComplete(character);
        }
    }, [onDeathAnimationComplete]);

    // Helper function to determine which sprite to use for a character
    const getSpriteInfo = (character: 'player' | 'ai') => {
        // If this character is taking damage, show death animation
        if (damagedCharacter === character) {
            return {
                spritesheet: character === 'player' ? 'pirate-death' : 'pirate-death2',
                frameCount: 4,
                // Slow down the animation to make it more visible (from 4fps to 3fps)
                fps: 3,
                loop: false, // Death animation should not loop
                onComplete: () => handleDeathAnimationComplete(character)
            };
        }
        // If this character is attacking, show attack animation
        else if (swordClash && attackingCharacter === character) {
            return {
                spritesheet: character === 'player' ? 'pirate-attack' : 'pirate-attack2',
                frameCount: 6,
                fps: 5,
                loop: false
            };
        }
        // Default to idle animation
        else {
            return {
                // Use different idle sprites based on the character
                spritesheet: character === 'player' ? 'pirate-idle' : 'pirate-idle2',
                frameCount: 5,
                fps: 5,
                loop: true
            };
        }
    };

    const playerSprite = getSpriteInfo('player');
    const aiSprite = getSpriteInfo('ai');

    return (
        <div className="battle-area nes-container is-dark" style={{ overflow: 'hidden' }}>
            <div className={`character pirate-player ${swordClash ? 'attack' : ''} ${damagedCharacter === 'player' ? 'damaged' : ''}`}>
                {imageError ? (
                    // Fallback to NES icons if sprite isn't working
                    <i className="nes-icon is-small nes-mario"></i>
                ) : (
                    <Sprite
                        spritesheet={`${process.env.PUBLIC_URL}/images/sprites/${playerSprite.spritesheet}.png`}
                        frameWidth={32}
                        frameHeight={32}
                        frameCount={playerSprite.frameCount}
                        fps={playerSprite.fps}
                        loop={playerSprite.loop}
                        isAnimating={true}
                        onComplete={playerSprite.onComplete}
                    />
                )}
            </div>
            <div className={`clash ${swordClash ? 'active' : ''}`}>
                {swordClash && (
                    <i className="nes-icon is-large star"></i>
                )}
            </div>
            <div className={`character pirate-ai ${swordClash ? 'attack' : ''} ${damagedCharacter === 'ai' ? 'damaged' : ''}`}>
                {imageError ? (
                    // Fallback to NES icons if sprite isn't working
                    <i className="nes-icon is-small nes-octocat"></i>
                ) : (
                    <Sprite
                        spritesheet={`${process.env.PUBLIC_URL}/images/sprites/${aiSprite.spritesheet}.png`}
                        frameWidth={32}
                        frameHeight={32}
                        frameCount={aiSprite.frameCount}
                        fps={aiSprite.fps}
                        loop={aiSprite.loop}
                        isAnimating={true}
                        onComplete={aiSprite.onComplete}
                        reversed={true} // AI sprites have frames ordered right-to-left
                    />
                )}
            </div>
        </div>
    );
};

export default BattleArea;
